import { ColumnDef, DataTable } from '@chiroup/components/ui/data-table';
import React, { useEffect, useMemo, useState } from 'react';
import eventLogService, { EventLogEvent } from '../../services/eventLog.service';

const PAGE_SIZE = 20;

const formatTimestamp = (ms: number): string => {
  return new Date(ms).toLocaleString();
};

type Props = {
  clinicId: number;
  metricKey: string;
  startDate: string;
  endDate: string;
};

const ExpandedMetricEvents: React.FC<Props> = ({ clinicId, metricKey, startDate, endDate }) => {
  const [events, setEvents] = useState<EventLogEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const fetchEvents = (skip: number, append: boolean) => {
    const setLoader = append ? setLoadingMore : setLoading;
    setLoader(true);
    eventLogService
      .listEvents(clinicId, {
        eventType: metricKey,
        startDate,
        endDate,
        limit: PAGE_SIZE,
        skip,
      })
      .then(res => {
        const newEvents = res?.data || [];
        setEvents(prev => (append ? [...prev, ...newEvents] : newEvents));
        setHasMore(res?.skip !== undefined);
        setLoader(false);
      })
      .catch(() => setLoader(false));
  };

  useEffect(() => {
    fetchEvents(0, false);
  }, [clinicId, metricKey, startDate, endDate]);

  const columns = useMemo<ColumnDef<EventLogEvent, unknown>[]>(
    () => [
      {
        accessorKey: 'occurredAt',
        header: 'Time',
        cell: ({ row }) => (
          <span className="text-xs whitespace-nowrap">
            {formatTimestamp(row.original.occurredAt)}
          </span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'userName',
        header: 'User',
        cell: ({ row }) => row.original.userName || row.original.userId || '—',
        enableSorting: false,
      },
      {
        accessorKey: 'locationId',
        header: 'Location',
        cell: ({ row }) => row.original.locationId || '—',
        enableSorting: false,
      },
      {
        accessorKey: 'metadata',
        header: 'Metadata',
        cell: ({ row }) => (
          <span className="text-xs text-gray-400 truncate block max-w-[300px]">
            {row.original.metadata ? JSON.stringify(row.original.metadata) : '—'}
          </span>
        ),
        enableSorting: false,
      },
    ],
    []
  );

  return (
    <div className="px-2 py-3">
      <DataTable
        columns={columns}
        data={events}
        size="compact"
        loading={loading}
        emptyState={<p className="text-gray-500">No events found</p>}
      />
      {hasMore && (
        <div className="mt-3 text-center">
          <button
            onClick={() => fetchEvents(events.length, true)}
            disabled={loadingMore}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            {loadingMore ? 'Loading...' : 'Load more events'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ExpandedMetricEvents;
